import type { Catalogue } from "../i18n";

/*
 * Minipi: a constructed language. Words are p + vowel syllables; the vowel carries the meaning
 * (macron ā ē ī ō ū ȳ, acute, grave). Sentences run verb + noun + complements + (subject).
 * Position changes meaning: pȳpípí = "clean water" or "to wash". Core vocabulary (English = Minipi):
 *
 * save = pāpí
 * delete = pòpù
 * cancel = pēpó
 * today = pūpá
 * yesterday = pūpè
 * tomorrow = pūpìp
 * chi = chi
 * add = pápā
 * new = pépē
 * edit = pípò
 * open = pópū
 * close = pūpò
 * yes = pāpā
 * no = pōpō
 * not = pò
 * ok = pópá
 * name = pīpé
 * note = pépù
 * notes = pépù
 * task = pàpí
 * tasks = pàpí
 * reminder = pīpà
 * reminders = pīpà
 * routine = pūpý
 * routines = pūpý
 * picture = pīpē
 * pictures = pīpē
 * ref = pēpī
 * refs = pēpī
 * reference = pēpī
 * references = pēpī
 * card = pāpē
 * cards = pāpē
 * deck = pāpō
 * draw = pōpé
 * drawing = pōpé
 * board = pūpē
 * layer = pēpū
 * frame = pápī
 * frames = pápī
 * color = pīpó
 * colour = pīpó
 * colors = pīpó
 * colours = pīpó
 * failed = pòpý
 * could = pāpè
 * can = pāpè
 * couldn = pòpāpè
 * loading = pūpí
 * saving = pāpípū
 * saved = pāpípē
 * waiting = pēpí
 * water = pȳpípí
 * well = pīpýpīpỳ
 * you = pīpī
 * your = pīpī
 * go = pìpý
 * nothing = pòpā
 * yet = pōpí
 * more = pūpā
 * less = pōpū
 * all = pāpū
 * none = pòpū
 * tag = pípā
 * tags = pípā
 * search = pépý
 * find = pépý
 * found = pépýpē
 * user = pīpū
 * people = pīpūpū
 * person = pīpū
 * share = pípó
 * shared = pípópē
 * file = pāpù
 * image = pīpēpī
 * settings = pópí
 * setting = pópí
 * language = pēpòpī
 * theme = pīpóp
 * day = pūpó
 * days = pūpó
 * week = pūpé
 * weeks = pūpé
 * month = pūpù
 * months = pūpù
 * year = pūpì
 * time = pūpāpī
 * hour = pūpō
 * minute = pūpī
 * back = pàpā
 * next = pēpā
 * done = pōpē
 * start = pápó
 * stop = pópáp
 * play = pápē
 * pause = pòpē
 * home = pōpì
 * plan = pīpá
 * studio = pōpú
 * practice = pípū
 * pipeline = pàpē
 * library = pīpù
 * core = pōpāp
 * map = pāpō
 * pin = pāpī
 * place = pēpē
 * places = pēpē
 * budget = pōpè
 * paid = pōpēpó
 * cost = pōpá
 * money = pōpó
 * message = pēpòp
 * messages = pēpòp
 * chat = pēpù
 * ask = pípē
 * say = pépó
 * says = pépó
 * copy = pūpēp
 * paste = pūpū
 * undo = pōpà
 * redo = pōpìp
 * erase = pēpà
 * clear = pēpóp
 * upload = pūpòp
 * download = pōpūp
 * send = pēpīp
 * sign = pípì
 * dark = pūpēpā
 * light = pīpò
 * night = pūpāpē
 * fog = pīpūp
 * favourite = pápū
 * favorite = pápū
 * error = pòpé
 * wrong = pòpéo
 * want = pīpí
 * need = pīpéo
 * make = pópē
 * create = pópēp
 * mark = pēpé
 * and = pē
 * or = pō
 * with = pī
 * for = pá
 * from = pú
 * in = pò
 * on = pù
 * at = pè
 * by = pà
 *
 * Other words are coined from the same syllables, always the same word for the same English word.
 * Small grammar words (the, a, to, of, is, it, this, that) are dropped.
 */

export const MP: Catalogue = {
  "failed": "pòpý",
  "saving": "pāpípū",
  "waiting": "pēpí",
  "today": "pūpá",
  "yesterday": "pūpè",
  "{days}d": "{days}pepa",
  "{weeks}w": "{weeks}pìpó",
  "unlabeled": "pēpōpù",
  "untagged": "pìpōpò",
  "loading more": "pūpí pūpā",
  "that's all {count}": "pāpū {count}",
  "shuffled": "pìpepi",
  "nothing to show": "pòpā pūpà",
  "chi is looking again": "chi papāpì pépa",
  "chi is looking at this one next": "chi papāpì pè pòpi pēpā",
  "could not index that ref": "pāpè pò pýpē pēpī",
  "details": "popēpē",
  "clips are not described, write your own": "pípà pò pipýpà, pipa pīpī pìpȳ",
  "chi left this one ({reason}), write your own": "chi pípỳ pòpi ({reason}), pipa pīpī pìpȳ",
  "chi is looking at this…": "chi papāpì pè…",
  "not described yet, write your own or tap spark": "pò pipýpà pōpí, pipa pīpī pìpȳ pō pēpú papȳ",
  "saved": "pāpípē",
  "could not save the description": "pāpè pò pāpí pópúpipí",
  "chi skips clips": "chi pīpè pípà",
  "described": "pipýpà",
  "chi is looking": "chi papāpì",
  "skipped": "pòpépù",
  "waiting on chi": "pēpí pù chi",
  "tagging {done}/{total} · {left} left": "pipípo {done}/{total} · {left} pípỳ",
  "tagged {done}/{total} · {left} left": "pèpȳpè {done}/{total} · {left} pípỳ",
  "all {total} tagged": "pāpū {total} pèpȳpè",
  "{shown} of {total}": "{shown} {total}",
  "{count} adding": "{count} pápā",
  "{count} failed": "{count} pòpý",
  "{count} to describe": "{count} pùpāpá",
  "busy": "pāpu",
  "tag": "pípā",
  "tap + to add pictures, drop files here, or use + ref in chrome": "pēpú + pápā pīpē, pàpý pāpù pōpỳ, pō pàpī + pēpī pò pýpýpỳ",
  "every ref already has your note": "pipā pēpī pòpópá pepō pīpī pépù",
  "nothing matches": "pòpā pápìpé",
  "could not load references": "pāpè pò pāpi pēpī",
  "video": "pīpa",
  "all described": "pāpū pipýpà",
  "could not read that file": "pāpè pò popó pāpù",
  "nothing usable there": "pòpā pỳpèpo pȳpà",
  "adding {count}": "pápā {count}",
  "could not import that file": "pāpè pò pūpepò pāpù",
  "showing {tag}": "pàpýpò {tag}",
  "chi is tagging {count} refs": "chi pipípo {count} pēpī",
  "every ref is tagged": "pipā pēpī pèpȳpè",
  "could not start tagging": "pāpè pò pápó pipípo",
  "delete this reference?": "pòpù pēpī?",
  "ref saved": "pēpī pāpípē",
  "ref save failed": "pēpī pāpí pòpý",
  "tagging done": "pipípo pōpē",
  "mode": "papū",
  "tools": "pàpỳ",
  "Tools": "Pàpỳ",
  "Talk to Chi": "Pypī Chi",
  "mode tuner, drag or tap a station": "papū pōpȳ, pýpu pō pēpú pýpipȳ",
  "Tool": "Pòpó",
  "Chi · direct line": "Chi · pàpopu pòpu",
  "{name}'s {what} · read only": "{name}'púpá {what} · popó pèpy",
  "could not open a print sheet": "pāpè pò pópū pāpy pùpū",
  "never": "pỳpí",
  "could not shrink that picture": "pāpè pò pípìpá pīpē",
  "could not read that picture": "pāpè pò popó pīpē",
  "chix is processing": "pypa pìpypýpỳ",
  "What should I work on next?": "Pòpí púpepú Pipè pāpa pù pēpā?",
  "nothing yet": "pòpā pōpí",
  "where it came from": "pípe pepu pú",
  "rent, phone…": "pópì, pypì…",
  "what was it": "pòpí pēpi",
  "mine": "pupú",
  "{name}'s": "{name}'púpá",
  "added to {name}'s budget": "pápā {name}'púpá pōpè",
  "added to your budget": "pápā pīpī pōpè",
  "couldn't add that": "pòpāpè pápā",
  "login with discord": "pypō pī pypàpà",
  "minipi #{id}": "pȳpèpè #{id}",
  "a minipi": "pȳpèpè",
  "{who} says": "{who} pépó",
  "{who} · art tip": "{who} · papi pỳpū",
  "pings": "pápý",
  "Late shift, {name}.": "Pópe pòpe, {name}.",
  "Morning, {name}.": "Pòpìpy, {name}.",
  "Ready when you are, {name}.": "Pàpȳ pỳpỳ pīpī, {name}.",
  "Evening, {name}.": "Pépȳpỳ, {name}.",
  "signal": "pópýpà",
  "sweet": "pòpà",
  "edge": "pipá",
  "dark fog, film grain and chi's cyan": "pūpēpā pīpūp, pepì pipū pē chi pīpỳ",
  "pale daytime fog, ink and deep teal": "pȳpy pūpòpú pīpūp, pìpā pē pápè pypȳ",
  "silent hill ash and an orange coal": "pipopa papý pépé pē pýpùpé pypó",
  "deep water, teal fog, a sonar ping": "pápè pȳpípí, pypȳ pīpūp, pūpo pópā",
  "rose paper and a hot pink signal": "pupè pỳpī pē pàpu popù pópýpà",
  "peach cream and a coral signal": "pèpà pàpù pē pīpo pópýpà",
  "mint milk with strawberry pink": "pāpò púpū pī pàpòpūpé popù",
  "magical girl night, violet fog, bubblegum pink": "pēpopu pepū pūpāpē, papỳpá pīpūp, pupúpū popù",
  "midnight sky in periwinkle and pink": "pỳpỳpa pupò pò pòpapúpē pē popù",
  "the otherworld: rust black and blood red": "pōpūpèpè: pupa pýpù pē pápi pipu",
  "night city chrome, warning yellow, red glitch": "pūpāpē pīpā pýpýpỳ, pòpȳpù pópápu, pipu pípùpe",
  "bruise purple fog and acid lime": "pòpepu pypùpē pīpūp pē pāpỳ pepȳ",
  "pure black and white, no mercy": "pōpi pýpù pē pápō, pōpō pìpī",
  "green crt, memory card manager at 2am": "pypā pōpu, pápàpè pāpē pūpýpō pè 2papu",
  "amber crt terminal, warm glow": "pāpú pōpu pỳpàpō, pypá pỳpá",
  "cream sketch paper and flipnote orange": "pàpù pỳpìpò pỳpī pē pīpúpō pýpùpé",
  "grey console, blue memory card screen": "pȳpó pīpópē, popỳ pápàpè pāpē pópùpo",
  "four shades of handheld green": "pèpý pepìpà pypàpa pypā",
  "none": "pòpū",
  "scanlines": "pýpīpì",
  "grid": "pypy",
  "dots": "pypý",
  "notebook": "pāpỳpú",
  "checker": "pipopú",
  "crosshair": "pepépi",
  "sparkles": "pīpāpi",
  "hazard": "popūpȳ",
  "starry sky": "pípápa pupò",
  "falling leaves": "pùpūpō pēpúpà",
  "sakura petals": "pùpýpé pāpìpà",
  "snow": "pỳpè",
  "rain": "pýpū",
  "fireflies": "pupỳpá",
  "moves gently, stays still with calm motion": "pipo pipìpi, pópy pỳpō pī pìpỳ pypépō",
  "pattern on": "pēpópý pù",
  "theme on": "pīpóp pù",
  "saved, type to replace": "pāpípē, popé pepýpù",
  "not set": "pò pepī",
  "token copied": "púpu pèpōpā",
  "make a new extension token? the old one stops working until you paste the new one in": "pópē pépē púpīpē púpu? pupū pòpi pópáp pùpūpè pypē pīpī pūpū pépē pòpi pò",
  "new token made": "pépē púpu pépy",
  "log out of chix on every device?": "pípȳ pȳpí pypa pù pipā pōpipa?",
  "$ dollar": "$ pépỳpé",
  "£ pound": "£ popy",
  "C$ canadian": "Pēpy$ pípūpi",
  "A$ australian": "$ pèpȳpīpò",
  "CHF swiss franc": "Pùpē pápí pype",
  "talking to plane": "pēpépū popu",
  "connected to {workspace}, now pick your boards": "pùpāpú {workspace}, pìpi pípī pīpī pūpē",
  "open plane": "pópū popu",
  "in safari, create an api token in profile settings, paste it below, then save": "pò pỳpopē, pópēp pupā púpu pò pìpipȳ pópí, pūpū papù, pìpé pāpí",
  "couldn't do that": "pòpāpè pipú",
  "google turned the map key away. check it's allowed on this site and maps javascript api is on": "pápópȳ pepāpỳ pāpō pūpỳ pepú. pépò pýpòpỳ pù pápù pē pāpō púpípépý pupā pù",
  "the map needs a key to draw, searches and routes still work below": "pāpō pīpéo pūpỳ pōpé, pépý pē pépápé pỳpō pāpa papù",
  "the map couldn't load. searches and routes still work below": "pāpō pòpāpè pāpi. pépý pē pépápé pỳpō pāpa papù",
  "you": "pīpī",
  "new pin": "pépē pāpī",
  "open now": "pópū pìpi",
  "closed now": "pūpò pìpi",
  "new": "pépē",
  "{rating} from 1 review": "{rating} pú 1 pēpìpē",
  "{rating} from {count} reviews": "{rating} pú {count} pìpepá",
  "no reviews yet": "pōpō pìpepá pōpí",
  "no parking info": "pōpō pépèpū popý",
  "no parking listed": "pōpō pépèpū pāpepà",
  "{place} is stop {letter}": "{place} pópáp {letter}",
  "save": "pāpí",
  "looking…": "papāpì…",
  "nothing found there. try further out or other words": "pòpā pépýpē pȳpà. popa pēpēpí pȳpí pō popì púpè",
  "pick something to look for, or search above": "pípī pōpēpà pōpù pá, pō pépý pỳpà",
  "{what} within {distance} of {place}": "{what} papapy {distance} {place}",
  "around {place}": "pipōpù {place}",
  "ten stops is the most": "papú pópáp papó",
  "taken out of saved": "pùpa pȳpí pāpípē",
  "remove {name}?": "popápu {name}?",
  "nothing saved yet": "pòpā pāpípē pōpí",
  "tap the map": "pēpú pāpō",
  "drop a pin": "pàpý pāpī",
  "edit {name}": "pípò {name}",
  "save pin": "pāpí pāpī",
  "my pin": "pupe pāpī",
  "pinned": "pépépā",
  "places, ratings, parking and routes come from google maps. it needs a key from your google cloud account, google gives a free allowance each month": "pēpē, pópūpo, pépèpū pē pépápé pēpì pú pápópȳ pāpō. pīpéo pūpỳ pú pīpī pápópȳ pȳpe pàpípō, pápópȳ popȳ popá pȳpèpū pùpá pūpù",
  "fractal hasn't added a google maps key yet, so the map can't look anything up": "papápī pòpá pápā pápópȳ pāpō pūpỳ pōpí, pepé pāpō pāpè pōpù pypúpo pupỳ",
  "within {distance}": "papapy {distance}",
  "this area": "pūpȳ",
  "couldn't find where you are, staying around {place}": "pòpāpè pépý pípe pīpī, pùpípu pipōpù {place}",
  "cyan": "pīpỳ",
  "violet": "papỳpá",
  "amber": "pāpú",
  "green": "pypā",
  "red": "pipu",
  "tap the map where the pin goes": "pēpú pāpō pípe pāpī pepù",
  "couldn't find where you are": "pòpāpè pépý pípe pīpī",
  "take this pin off the map?": "pȳpù pāpī pīpō pāpō?",
  "where i am": "pípe pipè papu",
  "home ({place})": "pōpì ({place})",
  "name this route": "pīpé pōpo",
  "{from} to {to}": "{from} {to}",
  "route saved": "pōpo pāpípē",
  "food": "pāpȳ",
  "pubs and bars": "pȳpè pē pýpí",
  "takeaway": "pàpépe",
  "shops": "pupý",
  "fuel": "pȳpỳ",
  "parking": "pépèpū",
  "pharmacy": "pūpúpu",
  "parks": "pīpy",
  "things to see": "pàpepe pypí",
  "cinema": "pīpope",
  "ev charging": "púpú papōpo",
  "drive": "pypỳ",
  "walk": "pépi",
  "cycle": "pāpì",
  "bus/train": "pópè/pēpȳ",
  "{amount} a month": "{amount} pūpù",
  "by {date}": "pà {date}",
  "monthly, stopped {date}": "pūpù, pápūpý {date}",
  "monthly from {date}": "pūpù pú {date}",
  "on {date}": "pù {date}",
  "added by you": "pápā pà pīpī",
  "added by {name}": "pápā pà {name}",
  "{done} paid of {total} · {left} left": "{done} pōpēpó {total} · {left} pípỳ",
  "paid off · {total}": "pōpēpó pīpō · {total}",
  "{done} saved of {total} · {left} to go": "{done} pāpípē {total} · {left} pìpý",
  "reached · {done}": "pỳpīpi · {done}",
  "in": "pò",
  "bill": "pipé",
  "cost": "pōpá",
  "debt": "pōpā",
  "goal": "popī",
  "remove {name}? its history goes too": "popápu {name}? pypápē pepù pēpe",
  "restart": "pàpepú",
  "stop": "pópáp",
  "paid": "pōpēpó",
  "payment logged": "pēpepū pōpàpō",
  "saved toward it": "pāpípē pupòpu",
  "took back the last one": "popi pàpā pipō pòpi",
  "{amount} left": "{amount} pípỳ",
  "{amount} saved": "{amount} pāpípē",
  "out": "pȳpí",
  "no monthly bills yet": "pōpō pūpù pipó pōpí",
  "owe": "pùpè",
  "{percent}% of {total}": "{percent}% {total}",
  "{amount} this month": "{amount} pūpù",
  "owed in total": "púpē pò pȳpa",
  "target": "pāpỳpé",
  "a month (optional)": "pūpù (pópȳpì)",
  "how much comes in": "pȳpȳ pàpó pupō pò",
  "how much": "pȳpȳ pàpó",
  "by": "pà",
  "since": "popí",
  "from": "pú",
  "on": "pù",
  "where it comes from": "pípe pupō pú",
  "rent, phone, adobe…": "pópì, pypì, pāpý…",
  "what you bought": "pòpí pīpī pápōpī",
  "who or what you owe": "pìpy pō pòpí pīpī pùpè",
  "what you're saving for": "pòpí pīpī pāpípū pá",
  "add": "pápā",
  "add to the budget": "pápā pōpè",
  "added": "pápā",
  "couldn't save that": "pòpāpè pāpí",
  "due: {title}": "pōpa: {title}",
  "unexpected \"{char}\"": "pìpupāpò \"{char}\"",
  "missing )": "pòpūpè )",
  "expected a number": "pỳpōpa pāpȳpī",
  "that goes to infinity": "pepù pỳpopò",
  "not set, add it in setup": "pò pepī, pápā pò pìpu",
  "{spent} of {budget} for tagging": "{spent} {budget} pá pipípo",
  "no cap on tagging": "pōpō pāpó pù pipípo",
  "{count} refs waiting since {time}": "{count} pēpī pēpí popí {time}",
  "none running": "pòpū pàpúpá",
  "{calls} calls · {input} in · {cached} cached · {output} out": "{calls} pìpū · {input} pò · {cached} pōpāpi · {output} pȳpí",
  "nothing yet for {month}": "pòpā pōpí pá {month}",
  "{calls} calls": "{calls} pìpū",
  "split starts with the next answer": "pupó pápó pī pēpā pépépe",
  "could not read claude usage": "pāpè pò popó pòpāpà pùpo",
  "all": "pāpū",
  "start {name}?": "pápó {name}?",
  "starting {name}...": "pápó {name}...",
  "{name} start sent": "{name} pápó pepo",
  "stop {name}?": "pópáp {name}?",
  "stopping {name}...": "pỳpupỳ {name}...",
  "{name} stop sent": "{name} pópáp pepo",
  "restart {name}?": "pàpepú {name}?",
  "restarting {name}...": "pàpópupò {name}...",
  "{name} restart sent": "{name} pàpepú pepo",
  "running": "pàpúpá",
  "stopped": "pápūpý",
  "crash looping": "pépỳ popépì",
  "starting": "pápó",
  "up {time}": "pupỳ {time}",
  "{count} restarts": "{count} pȳpỳpā",
  "{name} logs": "{name} pìpè",
  "loading...": "pūpí...",
  "{used} of {total}": "{used} {total}",
  "could not reach the server: {error}": "pāpè pò pùpȳ papýpé: {error}",
  "validating caddy...": "pàpùpapo pypé...",
  "caddy reloaded": "pypé pèpīpī",
  "{label}, one per line": "{label}, pòpi pīpȳ pòpu",
  "change picture": "pȳpúpe pīpē",
  "all {n}": "pāpū {n}",
  "unsorted": "pōpỳpò",
  "under a cent": "pȳpì pápe",
  "chi looking {done}/{total}": "chi papāpì {done}/{total}",
  "chi look {done}/{total}": "chi pōpù {done}/{total}",
  "chi looked": "chi pòpýpe",
  "chi has looked at every character picture": "chi pepō pòpýpe pè pipā pípìpỳ pīpē",
  "{n} more wait for another press.": "{n} pūpā pípō pá pipipỳ pēpè.",
  "chi has looked at {done} of {total} character pictures. look at the next {count} for about {price}? it goes through the half price batch, so it can take a while.": "chi pepō pòpýpe pè {done} {total} pípìpỳ pīpē. pōpù pè pēpā {count} pá pèpỳ {price}? pepù púpūpē púpā pópỳ pýpī, pepé pāpè pȳpù popē.",
  "chi is looking at {count} pictures": "chi papāpì pè {count} pīpē",
  "could not start chi": "pāpè pò pápó chi",
  "chi looks at 1 picture of {name} she hasn't seen, about {price}?": "chi pépo pè 1 pīpē {name} pỳpé pòpá pípá, pèpỳ {price}?",
  "chi looks at {count} pictures of {name} she hasn't seen, about {price}?": "chi pépo pè {count} pīpē {name} pỳpé pòpá pípá, pèpỳ {price}?",
  "chi has seen all {total} pictures of {name}. look at them again from scratch, about {price}?": "chi pepō pípá pāpū {total} pīpē {name}. pōpù pè pōpe pépa pú pȳpapā, pèpỳ {price}?",
  "chi is looking at {name}": "chi papāpì pè {name}",
  "picture {n}": "pīpē {n}",
  "adding 1 picture…": "pápā 1 pīpē…",
  "adding {n} pictures…": "pápā {n} pīpē…",
  "picture added": "pīpē pápā",
  "{n} pictures added": "{n} pīpē pápā",
  "could not add the picture": "pāpè pò pápā pīpē",
  "take this picture off the character?": "pȳpù pīpē pīpō pípìpỳ?",
  "picture removed": "pīpē pepopỳ",
  "could not remove the picture": "pāpè pò popápu pīpē",
  "sheet picture changed": "pùpū pīpē pōpápé",
  "could not change the picture": "pāpè pò pȳpúpe pīpē",
  "fetching characters from {source}…": "pūpàpý pēpópypa pú {source}…",
  "{n} found": "{n} pépýpē",
  "{n} added": "{n} pápā",
  "{n} updated": "{n} pùpēpy",
  "{n} duplicates merged": "{n} pòpapȳpó pỳpèpú",
  "{n} failed": "{n} pòpý",
  "{source} import failed": "{source} pūpepò pòpý",
  "aka {names}": "púpù {names}",
  "{n} pictures": "{n} pīpē",
  "merge into {name}": "pypú pỳpú {name}",
  "merge {other} into {name}? {other}'s sheet goes away and its pictures move to {name}.": "pypú {other} pỳpú {name}? {other}'púpá pùpū pepù pepú pē pīpē pỳpe {name}.",
  "{other} merged into {name}": "{other} pỳpèpú pỳpú {name}",
  "the sheet": "pùpū",
  "could not merge": "pāpè pò pypú",
  "character saved": "pípìpỳ pāpípē",
  "could not save": "pāpè pò pāpí",
  "delete {name}?": "pòpù {name}?",
  "1 block today": "1 papē pūpá",
  "{n} blocks today": "{n} pepypó pūpá",
  "resume": "pīpèpé",
  "back to drawing": "pàpā pōpé",
  "break time, stretch your hand": "pùpó pūpāpī, púpypo pīpī pípy",
  "focus block started": "pepè papē pápó",
  "take a break": "pȳpù pùpó",
  "type minutes, like 40 or 1h30": "popé pūpī, pèpū 40 pō 1pépī30",
  "drop this picture": "pàpý pīpē",
  "toss {hex}?": "pāpé {hex}?",
  "copied {hex}": "pèpōpā {hex}",
  "prompt copied": "pōpopō pèpōpā",
  "commissions": "pēpùpypȳ",
  "no commissions": "pōpō pēpùpypȳ",
  "comms money": "pupē pōpó",
  "{n} paid": "{n} pōpēpó",
  "{n} others": "{n} púpepō",
  "nothing paid yet": "pòpā pōpēpó pōpí",
  "that task is done or gone": "pàpí pōpē pō pìpò",
  "unlink {title}": "pīpipỳ {title}",
  "no task matches": "pōpō pàpí pápìpé",
  "no open tasks. add one in tasks first": "pōpō pópū pàpí. pápā pòpi pò pàpí pèpā",
  "for {client}": "pá {client}",
  "paid {day}": "pōpēpó {day}",
  "due {date}": "pōpa {date}",
  "commission saved": "pùpípèpỳ pāpípē",
  "commission added": "pùpípèpỳ pápā",
  "break": "pùpó",
  "unknown": "pēpòpú",
  "fapped {n} times": "pópīpé {n} pūpāpī",
  "no doodles match": "pōpō popopá pupī",
  "could not load the doodles": "pāpè pò pāpi popopá",
  "no picture on the clipboard": "pōpō pīpē pù pópupī",
  "pick a picture": "pípī pīpē",
  "you can look at the doodles, not add them": "pīpī pāpè pōpù pè popopá, pò pápā pōpe",
  "adding the doodle": "pápā pūpèpo",
  "adding {n} of {total}": "pápā {n} {total}",
  "{n} doodles added": "{n} popopá pápā",
  "could not add that": "pāpè pò pápā",
  "press ctrl+v or ⌘v to paste": "pēpè papè+pipà pō ⌘pipà pūpū",
  "couldn't read the clipboard, try ctrl+v or ⌘v": "pòpāpè popó pópupī, popa papè+pipà pō ⌘pipà",
  "gone": "pìpò",
  "remove {name}": "popápu {name}",
  "new doodle": "pépē pūpèpo",
  "doodle saved": "pūpèpo pāpípē",
  "delete this doodle?": "pòpù pūpèpo?",
  "could not delete": "pāpè pò pòpù",
  "by {name}": "pà {name}",
  "one a day per doodle, come back tomorrow": "pòpi pūpó pīpȳ pūpèpo, pēpì pàpā pūpìp",
  "today ✓ {n}": "pūpá ✓ {n}",
  "fapped {n}×": "pópīpé {n}pápỳ",
  "you {n}": "pīpī {n}",
  "could not count that": "pāpè pò pàpa",
  "random full body poses from the pocketbase library": "pīpopó pàpà popo pàpō pú pīpīpèpý pīpù",
  "{n} refs in the pool": "{n} pēpī pò pypò",
  "no refs match yet": "pōpō pēpī pupī pōpí",
  "could not fetch poses": "pāpè pò pupy pàpō",
  "play": "pápē",
  "no poses to draw from": "pōpō pàpō pōpé pú",
  "{n} poses done, nice": "{n} pàpō pōpē, pàpì",
  "{n} answered today": "{n} pápópa pūpá",
  "{n} day streak": "{n} pūpó pýpípò",
  "nothing due right now, {n} learning cards come back in {minutes}m": "pòpā pōpa pepỳ pìpi, {n} pōpupe pāpē pēpì pàpā pò {minutes}pupí",
  "today's cards are done, see you tomorrow": "pūpá pāpē pōpē, pypí pīpī pūpìp",
  "no cards to study, switch a deck on below": "pōpō pāpē pupù, pópípà pāpō pù papù",
  "{seen} of {total} seen · {solid} solid": "{seen} {total} pípá · {solid} pīpu",
  "study {deck}": "pupù {deck}",
  "could not load the deck": "pāpè pò pāpi pāpō",
  "could not save that answer": "pāpè pò pāpí pépépe",
  "could not save the deck setup": "pāpè pò pāpí pāpō pìpu",
  "basics": "púpȳpỳ",
  "favourites": "pápū",
  "likes and goals": "pypū pē pipē",
  "looks": "pépo",
  "identity": "pepēpē",
  "sexuality": "popùpá",
  "story": "papì",
  "for chi": "pá chi",
  "orientation": "pēpèpópù",
  "trivia": "pìpýpi",
  "created by": "pópēp pà",
  "world": "papo",
  "location": "pápēpa",
  "race": "pāpo",
  "occupation": "pīpȳpỳpè",
  "gender": "pípýpē",
  "height": "púpopi",
  "favourite food": "pápū pāpȳ",
  "smell": "pòpỳ",
  "pet peeve": "pápo pápì",
  "likes": "pypū",
  "dislikes": "pȳpapē",
  "look in short": "pōpù pò pipý",
  "body": "popo",
  "clothes": "pōpepì",
  "visual features": "pỳpỳpỳ púpīpȳ",
  "speaking": "pypīpe",
  "personality": "pōpìpūpā",
  "relationships": "pípápìpỳ",
  "items": "pépì",
  "skills": "pypàpè",
  "preferences": "pȳpúpòpi",
  "fetishes": "pipypī",
  "sexual trivia": "pepùpy pìpýpi",
  "rpg sheet": "pùpy pùpū",
  "fractal's notes for chi": "papápī pépù pá chi",
  "sees your {kinds}": "pùpý pīpī {kinds}",
  "sees nothing of yours": "pùpý pòpā pīpī",
  "{line}, adds to your {kinds}": "{line}, pápā pīpī {kinds}",
  "{name} can add to your {kind}": "{name} pāpè pápā pīpī {kind}",
  "{name} can only read your {kind}": "{name} pāpè pèpy popó pīpī {kind}",
  "{name} can see your {kind}": "{name} pāpè pypí pīpī {kind}",
  "{kind} hidden from {name}": "{kind} pēpìpé pú {name}",
  "{name} can see everything you can share": "{name} pāpè pypí pùpỳpypù pīpī pāpè pípó",
  "{name} sees nothing of yours now": "{name} pùpý pòpā pīpī pìpi",
  "share {kind} with {name}": "pípó {kind} pī {name}",
  "you don't have notes, pings or anything else to share yet": "pīpī pūpe pīpì pépù, pápý pō pypúpo pāpe pípó pōpí",
  "share all": "pípó pāpū",
  "share none": "pípó pòpū",
  "nobody else is in chix yet": "pupipy pāpe pò pypa pōpí",
  "shares their {kinds}": "pípó pépō {kinds}",
  "Calendar events": "Pépàpē pipópū",
  "Places and routes": "Pēpē pē pépápé",
  "your sticky notes": "pīpī pīpūpē pépù",
  "reminders, alarms and timers": "pīpà, pepupỳ pē pōpīpē",
  "your own events on the month": "pīpī pìpȳ pipópū pù pūpù",
  "plane and chix bot": "popu pē pypa púpỳ",
  "clients, prices, status, linked tasks and money stats": "pypùpā, pýpàpo, pépȳpe, pùpupe pàpí pē pōpó pèpē",
  "bills, costs, debts and goals": "pipó, pōpá, popà pē pipē",
  "what you keep up and your streaks": "pòpí pīpī pépu pupỳ pē pīpī pāpópà",
  "spots and routes you saved on the map": "pȳpu pē pépápé pīpī pāpípē pù pāpō",
  "Can add to it": "Pāpè pápā",
  "bills, costs, money in and payments, with their name on them. they can change only what they added": "pipó, pōpá, pōpó pò pē pápùpa, pī pépō pīpé pù pōpe. pìpì pāpè pȳpúpe pèpy pòpí pìpì pápā",
  "off": "pīpō",
  "{on} of {total} on · chi {spent} of {cap}": "{on} {total} pù · chi {spent} {cap}",
  "{on} of {total} on · chi {spent}": "{on} {total} pù · chi {spent}",
  "{feature} off for {name}": "{feature} pīpō pá {name}",
  "{name} can view {feature}": "{name} pāpè pípý {feature}",
  "{name} can edit {feature}": "{name} pāpè pípò {feature}",
  "{feature} for {name}": "{feature} pá {name}",
  "monthly chi cap, usd": "pūpù chi pāpó, papī",
  "{name}'s chi stops at {amount} a month": "{name}'púpá chi pópáp pè {amount} pūpù",
  "no cap for {name}": "pōpō pāpó pá {name}",
  "{spent} over {count} answers this month. 0 means no cap": "{spent} papé {count} pàpīpo pūpù. 0 pỳpò pōpō pāpó",
  "remove {name}? they get signed out. their own notes and chats stay on the server if you add them back": "popápu {name}? pìpì pỳpì pípì pȳpí. pépō pìpȳ pépù pē pēpù pùpò pù papýpé pupà pīpī pápā pōpe pàpā",
  "{name} removed": "{name} pepopỳ",
  "just you for now": "pȳpé pīpī pá pìpi",
  "{name} can sign in now. they start with chi, notes, pings and routines": "{name} pāpè pípì pò pìpi. pìpì pápó pī chi, pépù, pápý pē pūpý",
  "Calendar and pings": "Pépàpē pē pápý",
  "Chi speaker": "Chi púpapu",
  "Discord messages": "Pypàpà pēpòp",
  "Keys and server setup": "Pȳpē pē papýpé pìpu",
  "their own chat and memory, on your claude account": "pépō pìpȳ pēpù pē pápàpè, pù pīpī pòpāpà pàpípō",
  "the shared ref library": "pípópē pēpī pīpù",
  "the shared oc sheets": "pípópē pȳpò pēpīpī",
  "their own": "pépō pìpȳ",
  "plane and chix bot, their own": "popu pē pypa púpỳ, pépō pìpȳ",
  "timer, palette, prompts, comms and their money stats": "pāpà, pípūpỳ, pāpāpā, pupē pē pépō pōpó pèpē",
  "their own bills, costs, debts and goals": "pépō pìpȳ pipó, pōpá, popà pē pipē",
  "places nearby, routes and their saved spots, on your google maps key": "pēpē papȳpè, pépápé pē pépō pāpípē pȳpu, pù pīpī pápópȳ pāpō pūpỳ",
  "gesture drawing and japanese": "pàpùpí pōpé pē pīpépý",
  "the shared doodle wall": "pípópē pūpèpo pàpū",
  "their own scan library": "pépō pìpȳ popā pīpù",
  "their pings ring at home, chi answers their voice": "pépō pápý pýpá pè pōpì, chi pàpīpo pépō pópo",
  "chi can dm your contacts for them": "chi pāpè papà pīpī pùpipī pá pōpe",
  "vps panel and bot controls": "pípi pepý pē púpỳ pépipi",
  "every log line": "pipā pípȳ pòpu",
  "spend and billing": "pýpe pē pūpépi",
  "api keys, ssh": "pupā pȳpē, pópu",
  "this dashboard": "pōpūpý",
  "tap to upload": "pēpú pūpòp",
  "{count} kept": "{count} pípù",
  "uploading {n} of {total}": "pūpòp {n} {total}",
  "scan kept": "popā pípù",
  "{count} scans kept": "{count} pỳpý pípù",
  "upload failed": "pūpòp pòpý",
  "the brother is scanning": "popúpō pupōpā",
  "scan failed": "popā pòpý",
  "saving {n} of {total}": "pāpípū {n} {total}",
  "added to scans": "pápā pỳpý",
  "could not save that": "pāpè pò pāpí",
  "pdf scan, use download to open it": "pōpò popā, pàpī pōpūp pópū",
  "delete this scan?": "pòpù popā?",
  "days apart": "pūpó pùpú",
  "times a week, any days": "pūpāpī pūpé, popò pūpó",
  "day of the month": "pūpó pūpù",
  "added by another part of chix, ticked off there too": "pápā pà pipipỳ pýpý pypa, popapī pīpō pȳpà pēpe",
  "delete {title} and its history?": "pòpù {title} pē pypápē?",
  "no routines": "pōpō pūpý",
  "ChiX routine": "Pypa pūpý",
  "time for {title}": "pūpāpī pá {title}",
  "weekdays": "púpepù",
  "every {n} days": "pipā {n} pūpó",
  "{n}x a week": "{n}pìpí pūpé",
  "monthly on day {day}": "pūpù pù pūpó {day}",
  "{done}/{times} this week": "{done}/{times} pūpé",
  "ping {time}": "pópā {time}",
  "{n}w streak": "{n}pìpó pýpípò",
  "{n} streak": "{n} pýpípò",
  "{title} done": "{title} pōpē",
  "sticky {date}": "pīpūpē {date}",
  "saved {time}": "pāpípē {time}",
  "nothing coming up": "pòpā púpàpú pupỳ",
  "edit chix task": "pípò pypa pàpí",
  "new chix task": "pépē pypa pàpí",
  "saving...": "pāpípū...",
  "mark \"{title}\" as done?": "pēpé \"{title}\" pèpé pōpē?",
  "urgent": "pāpỳpì",
  "high": "pipi",
  "medium": "pápūpȳ",
  "low": "pùpé",
  "{count} shown, updated {time}": "{count} pipí, pùpēpy {time}",
  "nothing pending": "pòpā pàpepȳ",
  "night": "pūpāpē",
  "fog": "pīpūp",
  "ember": "pīpi",
  "abyss": "pypi",
  "petal": "púpȳ",
  "peach": "pèpà",
  "mint": "pāpò",
  "lilac": "pùpì",
  "starlight": "pýpīpà",
  "venom": "pòpa",
  "pocket": "pòpèpē",
  "{title} reached, tap done to cross it off": "{title} pỳpīpi, pēpú pōpē pùpà pīpō",
  "delete {title}? it won't come back": "pòpù {title}? pépȳ pēpì pàpā",
  "done · {n}": "pōpē · {n}",
  "no goals": "pōpō pipē",
  "this one's done, saving keeps it crossed out": "pòpi pōpē, pāpípū pípa pīpopí pȳpí",
  "no goals on the go": "pōpō pipē pù pìpý",
  "add a couple of options": "pápā pȳpūpỳ popépu",
  "last one standing: {label}": "pipō pòpi pèpepá: {label}",
  "everything's been knocked out": "pùpỳpypù pépè pupỳpò pȳpí",
  "spinning": "pōpēpý",
  "the wheel says": "pūpi pépó",
  "new wheel": "pépē pūpi",
  "knocked out": "pupỳpò pȳpí",
  "{n} on the wheel, {out} knocked out": "{n} pù pūpi, {out} pupỳpò pȳpí",
  "{n} on the wheel": "{n} pù pūpi",
  "just now": "pȳpé pìpi",
  "{n}m ago": "{n}pupí púpý",
  "{n}h ago": "{n}pépī púpý",
  "ready": "pàpȳ",
  "flick the wheel or press spin": "pòpo pūpi pō pēpè pípu",
  "add a couple of options to spin": "pápā pȳpūpỳ popépu pípu",
  "a wheel holds {max} options": "pūpi pȳpá {max} popépu",
  "only {room} fit, a wheel holds {max}": "pèpy {room} púpó, pūpi pȳpá {max}",
  "my characters": "pupe pēpópypa",
  "there aren't enough characters yet": "pȳpà pỳpó pópúpù pēpópypa pōpí",
  "which oc": "pipī pȳpò",
  "{name} copy": "{name} pūpēp",
  "delete the wheel \"{name}\"?": "pòpù pūpi \"{name}\"?",
  "yes or no": "pāpā pō pōpō",
  "what to draw": "pòpí pōpé",
  "dinner": "popȳpā",
  "dice": "pȳpý",
  "who does it": "pìpy pòpè",
  "yes": "pāpā",
  "gesture warm up": "pàpùpí pypá pupỳ",
  "portrait": "púpípi",
  "an oc": "pȳpò",
  "hands and feet": "pípí pē pāpá",
  "background": "pèpòpapȳ",
  "comic page": "pēpý pìpe",
  "redraw an old piece": "pīpāpā pupū pepá",
  "burgers": "pópápy",
  "noodles": "pypèpì",
  "cook something": "pùpù pōpēpà",
  "yellow": "pópápu",
  "orange": "pýpùpé",
  "lime": "pepȳ",
  "teal": "pypȳ",
  "blue": "popỳ",
  "purple": "pypùpē",
  "pink": "popù",
  "white": "pápō",
  "black": "pýpù",
  "sky": "pupò",
  "slate": "pȳpo",
  "edit message": "pípò pēpòp",
  "delete your message? it goes for everyone": "pòpù pīpī pēpòp? pepù pá pāpāpu",
  "delete {name}'s message? it goes for everyone": "pòpù {name}'púpá pēpòp? pepù pá pāpāpu",
  "couldn't delete that": "pòpāpè pòpù",
  "couldn't post that": "pòpāpè pùpỳ",
  "1 message": "1 pēpòp",
  "{n} messages": "{n} pēpòp",
  "someone": "pōpūpy",
  "edited": "pípò",
  "now": "pìpi",
  "{n}m": "{n}pupí",
  "{n}h": "{n}pépī",
  "+{n} more": "+{n} pūpā",
  "couldn't switch": "pòpāpè pópípà",
  "switch": "pópípà",
  "profile": "pìpipȳ",
  "that isn't a picture": "pope pīpē",
  "picture saved": "pīpē pāpípē",
  "upload picture": "pūpòp pīpē",
  "use {name}'s discord picture": "pàpī {name}'púpá pypàpà pīpē",
  "remove picture": "popápu pīpē",
  "owned by": "pupȳ pà",
  "remove {name} as an owner": "popápu {name} pèpé pýpà",
  "owner removed": "pýpà pepopỳ",
  "another discord user id": "pipipỳ pypàpà pīpū pepà",
  "discord user id": "pypàpà pīpū pepà",
  "add owner": "pápā pýpà",
  "they can sign in as {name} now": "pìpì pāpè pípì pò pèpé {name} pìpi",
  "a discord account that owns an alter can sign in and switch to it, so give the alter's own account here": "pypàpà pàpípō pýpè púpí pāpè pípì pò pē pópípà, pepé papā púpí pìpȳ pàpípō pōpỳ",
  "she/her, they/them…": "pỳpé/pàpé, pìpì/pōpe…",
  "a line about them": "pòpu pèpỳ pōpe",
  "pronouns": "pỳpupè",
  "about": "pèpỳ",
  "be {name}": "pepí {name}",
  "{name} is part of {system}": "{name} pýpý {system}",
  "back to my profile": "pàpā pupe pìpipȳ",
  "remove {name}? their notes and chats stay on the server if you add them back": "popápu {name}? pépō pépù pē pēpù pùpò pù papýpé pupà pīpī pápā pōpe pàpā",
  "no alters yet. add the first one above": "pōpō pāpopì pōpí. pápā pèpā pòpi pỳpà",
  "{name} added": "{name} pápā",
  "size: {size}, change": "pýpỳ: {size}, pȳpúpe",
  "small": "pypè",
  "half": "púpā",
  "wide": "pápu",
  "hide": "pỳpē",
  "move earlier": "pỳpe pýpàpý",
  "move later": "pỳpe pìpù",
  "hold to drag": "pòpy pýpu",
  "arrange home": "pypypà pōpì",
  "ask chi": "pípē chi",
  "inbox": "pepe",
  "gauges": "pēpépa",
  "message board": "pēpòp pūpē",
  "spin": "pípu",
  "{n} lines": "{n} pàpy",
  "live": "pépū",
  "no shared boards yet. make one and everyone can draw on it together": "pōpō pípópē pūpē pōpí. pópē pòpi pē pāpāpu pāpè pōpé pù pòpýpí",
  "no shared boards yet. Fractal can make one": "pōpō pípópē pūpē pōpí. Papápī pāpè pópē pòpi",
  "{shared} shared · {mine} yours": "{shared} pípópē · {mine} pīpī",
  "could not load the boards": "pāpè pò pāpi pūpē",
  "could not make a board": "pāpè pò pópē pūpē",
  "that board was deleted": "pūpē pēpi pòpù",
  "shared board": "pípópē pūpē",
  "board": "pūpē",
  "offline, will send when back": "pùpūpý, pepò pēpīp pỳpỳ pàpā",
  "that's someone else's, only they can change it": "pōpūpy pāpe, pèpy pìpì pāpè pȳpúpe",
  "lost the board": "pópȳ pūpē",
  "that layer is hidden, show it to draw": "pēpū pēpìpé, pūpà pōpé",
  "that layer is hidden, show it to write": "pēpū pēpìpé, pūpà pipa",
  "that layer is locked": "pēpū pepỳpī",
  "that layer is {who}'s, ask them to let you draw on it": "pēpū {who}'púpá, pípē pōpe pỳpa pīpī pōpé pù",
  "that layer has other people's lines, use the stroke eraser": "pēpū pepō popì pīpūpū pàpy, pàpī pèpópó popōpō",
  "pencil found, fingers now move the board": "pipúpú pépýpē, pȳpépī pìpi pỳpe pūpē",
  "{n} frames": "{n} pápī",
  "open": "pópū",
  "empty": "pepi",
  "this layer is yours": "pēpū pīpī",
  "{who}'s layer": "{who}'púpá pēpū",
  ", open to everyone": ", pópū pāpāpu",
  ", only they draw here": ", pèpy pìpì pōpé pōpỳ",
  "frames": "pápī",
  "animate": "pépàpỳ",
  "layer {n}": "pēpū {n}",
  "delete {name}? you can undo it": "pòpù {name}? pīpī pāpè pōpà",
  "{name} cleared, undo brings it back": "{name} pēpóp, pōpà pípúpí pàpā",
  "{n} frames is the most a layer holds": "{n} pápī papó pēpū pȳpá",
  "delete frame {n}? you can undo it": "pòpù pápī {n}? pīpī pāpè pōpà",
  "frame {n}": "pápī {n}",
  "frames per second": "pápī pīpȳ popópō",
  "onion skin {state}": "papy pépí {state}",
  "shared · look only": "pípópē · pōpù pèpy",
  "shared · live": "pípópē · pépū",
  "private": "pipùpò",
  "paper {color}": "pỳpī {color}",
  "could not make the picture": "pāpè pò pópē pīpē",
  "draw something first": "pōpé pōpēpà pèpā",
  "posted to the doodle wall": "pìpupò pūpèpo pàpū",
  "could not post it": "pāpè pò pùpỳ",
  "delete {name}? this can't be undone": "pòpù {name}? pāpè pepí pīpīpú",
  "delete {name}? this can't be undone, and it goes for everyone": "pòpù {name}? pāpè pepí pīpīpú, pē pepù pá pāpāpu",
  "could not delete it": "pāpè pò pòpù",
  "could not open that board": "pāpè pò pópū pūpē",
  "brush": "pepē",
  "erase": "pēpà",
  "select": "pèpūpū",
  "shape": "pipe",
  "text": "pỳpi",
  "move": "pỳpe",
  "line": "pòpu",
  "arrow": "pùpí",
  "box": "pỳpā",
  "ellipse": "pēpúpý",
  "rub out": "pìpa pȳpí",
  "whole strokes": "púpī púpépù",
  "duplicate": "pìpìpỳ",
  "delete": "pòpù",
  "done": "pōpē",
  "drag to pick, tap one stroke": "pýpu pípī, pēpú pòpi pèpópó",
  "show": "pūpà",
  "lock": "papỳ",
  "unlock": "pèpōpú",
  "move up": "pỳpe pupỳ",
  "move down": "pỳpe pȳpī",
  "stop playing": "pópáp pápē",
  "play this layer": "pápē pēpū",
  "previous frame": "pùpúpá pápī",
  "next frame": "pēpā pápī",
  "new empty frame": "pépē pepi pápī",
  "copy this frame": "pūpēp pápī",
  "delete this frame": "pòpù pápī",
  "move this frame earlier": "pỳpe pápī pýpàpý",
  "move this frame later": "pỳpe pápī pìpù",
  "plain": "pòpì",
  "lines": "pàpy",
  "pen": "pàpò",
  "pencil": "pipúpú",
  "ink": "pìpā",
  "marker": "pēpé",
  "glow": "pỳpá",
  "eraser": "popōpō",
  "water": "pȳpípí",
  "air": "pìpú",
  "spray": "pỳpȳ",
  "coal": "pypó",
  "chalk": "pèpō",
  "crayon": "pòpapȳ",
  "rainbow": "pópāpì",
  "glitter": "pápīpè",
  "neon": "pìpē",
  "boil": "pýpā",
  "wave": "pìpō",
  "flame": "pápé",
  "march": "pȳpū",
  "pulse": "púpy",
  "draw": "pōpé",
  "paint": "pýpì",
  "texture": "pūpépá",
  "steady": "pepýpó",
  "pressure": "púpýpȳ",
  "soft": "pèpe",
  "spread": "pìpēpī",
  "reset this brush": "pùpō pepē",
  "multiply": "pèpopú",
  "screen": "pópùpo",
  "overlay": "pepāpè",
  "soft light": "pèpe pīpò",
  "hard light": "pùpā pīpò",
  "darken": "pīpápé",
  "lighten": "pāpópý",
  "color dodge": "pīpó pùpe",
  "color burn": "pīpó pìpo",
  "difference": "púpípìpā",
  "exclusion": "pōpōpý",
  "Goals": "Pipē",
  "me": "pèpa",
  "Profile": "Pìpipȳ",
  "Message board": "Pēpòp pūpē",
  "Roulette": "Pípúpē",
  "Whiteboards": "Pépāpàpỳ",
  "nothing on the board yet": "pòpā pù pūpē pōpí",
  "add to a": "pápā",
  "default": "pápìpe",
  "out of": "pȳpí",
  "no goals yet. add one for a chapter to finish, a page count to hit or anything you're working towards": "pōpō pipē pōpí. pápā pòpi pá pīpýpī púpúpú, pìpe pàpa pīpe pō pypúpo pīpī pùpūpè pypúpè",
  "kana is the standard gojūon table. kanji are the 2,211 jlpt n5 to n1 kanji, meanings and readings from kanjidic2 (edrdg, cc by-sa 4.0), levels from jonathan waller's jlpt lists (cc by)": "pepā pāpỳpy pópùpā pỳpy. pūpa 2,211 pépú pópó5 pópó1 pūpa, pȳpopī pē pùpỳpá pú pùpapi2 (pòpò, pèpi pà-pípè 4.0), papȳpu pú pipòpa pupapē pépú popō (pèpi pà)",
  "bring all back": "pápȳ pāpū pàpā",
  "slices": "pápỳpí",
  "history": "pypápē",
  "options": "popépu",
  "pasting a list adds them all. pizza *3 makes pizza three times as likely, and the weights below set it per slice": "pūpū pupì pápā pōpe pāpū. popú *3 pópē popú pèpù pūpāpī pèpé popāpu, pē pápỳpa papù pepī pīpȳ pýpo",
  "no options yet. add a few, or start from a preset in options": "pōpō popépu pōpí. pápā pèpí, pō pápó pú pìpùpu pò popépu",
  "shuffle": "pápypu",
  "even odds": "pēpō pypù",
  "theme colours": "pīpóp pīpó",
  "tally": "pipì",
  "last spins": "pipō pópò",
  "nothing spun on this wheel yet": "pòpā púpi pù pūpi pōpí",
  "after a spin": "pēpò pípu",
  "keep it": "pépu",
  "knock it out": "pepy pȳpí",
  "knock it out takes each winner off the wheel until you bring them all back, good for turns and tournaments": "pepy pȳpí pýpú pùpá pupápa pīpō pūpi pypē pīpī pápȳ pōpe pāpū pàpā, pípo pá pýpō pē pýpopupō",
  "spin length": "pípu popýpo",
  "quick": "pàpá",
  "clicks and a jingle": "pàpīpa pē pāpypa",
  "new wheel from": "pépē pūpi pú",
  "copy this wheel": "pūpēp pūpi",
  "delete wheel": "pòpù pūpi",
  "winners are picked with your device's secure random, in proportion to each slice's weight, so a slice that looks twice as big really is twice as likely": "pypỳpì pópàpà pī pīpī pōpipa pùpāpȳ pīpopó, pò pòpùpípi pùpá pýpo pēpȳpy, pepé pýpo pépo púpì pèpé pȳpō pupipi púpì pèpé popāpu",
  "wheel": "pūpi",
  "the wheel, flick it to spin": "pūpi, pòpo pípu",
  "add options, one per line": "pápā popépu, pòpi pīpȳ pòpu",
  "option": "pàpepé",
  "weight": "pēpȳpy",
  "less likely": "pōpū popāpu",
  "more likely": "pūpā popāpu",
  "less": "pōpū",
  "more": "pūpā",
  "shared": "pípópē",
  "new board": "pépē pūpē",
  "mine · private": "pupú · pipùpò",
  "no boards of your own yet. only you ever see these": "pōpō pūpē pīpī pìpȳ pōpí. pèpy pīpī pýpó pypí pùpī",
  "layers": "pēpū",
  "opacity": "pupàpā",
  "blend": "pipy",
  "let others draw on this layer": "pỳpa púpepō pōpé pù pēpū",
  "brush ·": "pepē ·",
  "pick from the board": "pípī pú pūpē",
  "recent": "pápēpó",
  "any colour": "popò pīpó",
  "paper": "pỳpī",
  "only the pencil draws, fingers move and zoom the board": "pèpy pipúpú pōpé, pȳpépī pỳpe pē púpò pūpē",
  "fit": "púpó",
  "save png": "pāpí pàpi",
  "post to doodles": "pùpỳ popopá",
  "two fingers pinch and move, tap two fingers to undo, three to redo. on a keyboard: space drags, [ and ] size, ⌘z undo, k brushes, x erases whole strokes, t text, v select, u shapes, comma and full stop step frames, backslash plays": "pūpapī pȳpépī pópú pē pỳpe, pēpú pūpapī pȳpépī pōpà, pèpù pōpìp. pù pàpāpù: popū pápò, [ pē ] pýpỳ, ⌘pýpò pōpà, papò pepèpú, pìpí pēpà púpī púpépù, pópa pỳpi, pipà pèpūpū, pépá pypòpi, pèpȳ pē pàpà pópáp pèpú pápī, pòpipú pápē",
  "delete board": "pòpù pūpē",
  "Board": "Pūpē",
  "everyone on chi sees this": "pāpāpu pù chi pùpý",
  "nothing on the board yet. post the first line": "pòpā pù pūpē pōpí. pùpỳ pèpā pòpu",
  "who's here": "pìpy pōpỳ",
  "who is here": "pìpy pōpỳ",
  "jot to inbox": "pupu pepe",
  "say something to everyone": "pépó pōpēpà pāpāpu",
  "message for the board": "pēpòp pá pūpē",
  "finish the chapter 2 sprites…": "púpúpú pīpýpī 2 pȳpepò…",
  "why, or what done looks like, optional": "pápà, pō pòpí pōpē pépo pèpū, pópȳpì",
  "progress": "pìpèpà",
  "counting what": "pipýpi pòpí",
  "a board everyone can draw on together": "pūpē pāpāpu pāpè pōpé pù pòpýpí",
  "a board only you can see": "pūpē pèpy pīpī pāpè pypí",
  "text on the board": "pỳpi pù pūpē",
  "back to the boards": "pàpā pūpē",
  "undo, or tap with two fingers": "pōpà, pō pēpú pī pūpapī pȳpépī",
  "redo": "pōpìp",
  "redo, or tap with three fingers": "pōpìp, pō pēpú pī pèpù pȳpépī",
  "fit the drawing on screen": "púpó pōpé pù pópùpo",
  "fit to the drawing": "púpó pōpé",
  "board settings": "pūpē pópí",
  "brush size": "pepē pýpỳ",
  "say something to everyone…": "pépó pōpēpà pāpāpu…",
  "new alter name": "pépē púpí pīpé",
  "all {count} presets are taken, unpin one first": "pāpū {count} pupopù pùpa, pypo pòpi pèpā",
  "could not change the owner": "pāpè pò pȳpúpe pýpà",
  "description": "pópúpipí",
  "groups": "popípó",
  "large": "pepó",
  "layout": "pòpēpá",
  "mine {n}": "pupú {n}",
  "owners": "popépù",
  "presets": "pupopù",
  "reversed": "pōpùpu",
  "sort by": "pépe pà",
  "tap the star on a key to pin it. pins sit above the dock as presets": "pēpú pèpò pù pūpỳ pāpī. pāpī pūpy pỳpà pùpi pèpé pupopù",
  "unknown date": "pēpòpú pàpo",
  "{name} is not yours any more": "{name} pò pīpī popò pūpā",
  "{name} is yours now": "{name} pīpī pìpi",
  "Grid": "Pypy",
  "Masonry": "Pùpúpó",
  "Rows": "Pípú",
  "Big grid": "Pȳpō pypy",
  "Wall": "Pàpū",
  "Posts": "Pipù",
  "Shuffle": "Pápypu",
  "random order, rolled once while it is open": "pīpopó pýpy, pypepū pèpá popē pópū",
  "Newest": "Pōpēpa",
  "latest saved first": "pupupà pāpípē pèpā",
  "Oldest": "Pupìpì",
  "earliest saved first": "pipypò pāpípē pèpā",
  "Most tagged": "Papó pèpȳpè",
  "refs with the most tags first": "pēpī pī papó pípā pèpā",
  "Undescribed first": "Pèpāpápu pèpā",
  "what has no description yet comes first": "pòpí pepō pōpō pópúpipí pōpí pupō pèpā",
  "By tag": "Pà pípā",
  "grouped under the most used tag": "pȳpìpo pȳpì papó pēpá pípā",
  "By author": "Pà pìpēpà",
  "grouped A to Z": "pȳpìpo Pýpò",
  "By month": "Pà pūpù",
  "grouped by the month you saved them": "pȳpìpo pà pūpù pīpī pāpípē pōpe",
  "By type": "Pà popé",
  "stills, gifs and clips apart": "pápȳpè, pȳpi pē pípà pùpú",
  "sign in with discord · fractal and pauline": "pípì pò pī pypàpà · papápī pē pypòpī",
  "continue with discord": "pipìpỳ pī pypàpà",
  "use": "pàpī",
  "Ready when you are.": "Pàpȳ pỳpỳ pīpī.",
  "unit": "pèpó",
  "chi · online": "chi · papépú",
  "clear": "pēpóp",
  "tip of the day": "pỳpū pūpó",
  "another one": "pipipỳ pòpi",
  "add to a budget": "pápā pōpè",
  "every month": "pipā pūpù",
  "up next": "pupỳ pēpā",
  "all tasks": "pāpū pàpí",
  "nothing due, go draw": "pòpā pōpa, pìpý pōpé",
  "routines": "pūpý",
  "routines done for today": "pūpý pōpē pá pūpá",
  "notes": "pépù",
  "no notes yet": "pōpō pépù pōpí",
  "Tasks": "Pàpí",
  "chix task": "pypa pàpí",
  "Notes": "Pépù",
  "note": "pépù",
  "scratchpad": "púpopòpì",
  "edit": "pípò",
  "view": "pípý",
  "random": "pīpopó",
  "newest": "pōpēpa",
  "oldest": "pupìpì",
  "by tag": "pà pípā",
  "stills": "pápȳpè",
  "moving": "pypapó",
  "chi's description": "chi pópúpipí",
  "your note": "pīpī pépù",
  "ctrl+enter saves and goes next": "papè+pópi pāpí pē pepù pēpā",
  "edit tags": "pípò pípā",
  "close": "pūpò",
  "next": "pēpā",
  "Chi online. What are we drawing?": "Chi papépú. Pòpí pēpa pōpé?",
  "what should I work on?": "pòpí púpepú Pipè pāpa pù?",
  "pose refs": "pōpy pēpī",
  "pick an oc": "pípī pȳpò",
  "comms": "pupē",
  "Routines": "Pūpý",
  "routine": "pūpý",
  "every day": "pipā pūpó",
  "on these days": "pù pùpī pūpó",
  "every few days": "pipā pèpí pūpó",
  "times a week": "pūpāpī pūpé",
  "once a month": "pèpá pūpù",
  "mon": "pupi",
  "tue": "pùpāpỳ",
  "wed": "pèpo",
  "thu": "púpō",
  "fri": "pepípy",
  "sat": "pōpý",
  "sun": "pópō",
  "ping at": "pópā pè",
  "paused": "pòpē",
  "cancel": "pēpó",
  "nothing on today": "pòpā pù pūpá",
  "other days": "popì pūpó",
  "no routines yet. add one for a daily sketch, a weekly stream or anything you want to keep up": "pōpō pūpý pōpí. pápā pòpi pá pỳpo pỳpìpò, pūpé pȳpȳpa pō pypúpo pīpī pīpí pépu pupỳ",
  "Pings": "Pápý",
  "reminder": "pīpà",
  "alarm": "pòpī",
  "timer": "pāpà",
  "once": "pèpá",
  "daily": "pỳpo",
  "weekly": "pūpé",
  "set": "pepī",
  "no pings yet. set one for a stream, a deadline or a break": "pōpō pápý pōpí. pepī pòpi pá pȳpȳpa, popōpū pō pùpó",
  "Session": "Pépúpȳ",
  "focus": "pepè",
  "start": "pápó",
  "tap the time to type your own, like 40 or 1h30": "pēpú pūpāpī popé pīpī pìpȳ, pèpū 40 pō 1pépī30",
  "0 blocks today": "0 pepypó pūpá",
  "Palette": "Pípūpỳ",
  "keep": "pépu",
  "tap a swatch to copy it, hold to toss it": "pēpú pēpūpù pūpēp, pòpy pāpé",
  "save prompt": "pāpí pōpopō",
  "tap a card to copy the whole prompt": "pēpú pāpē pūpēp púpī pōpopō",
  "Comms": "Pupē",
  "tap a commission to edit it": "pēpú pùpípèpỳ pípò",
  "list": "pupì",
  "money": "pōpó",
  "new commission": "pépē pùpípèpỳ",
  "sketch": "pỳpìpò",
  "wip": "púpé",
  "review": "pēpìpē",
  "reference pics": "pēpī papō",
  "this month": "pūpù",
  "this year": "pūpì",
  "to be paid": "pepí pōpēpó",
  "paid by month": "pōpēpó pà pūpù",
  "by client": "pà pupìpū",
  "by status": "pà pépȳpe",
  "a commission counts on the day it was marked paid. change that day on its card": "pùpípèpỳ pèpùpí pù pūpó pēpi pēpé pōpēpó. pȳpúpe pūpó pù pāpē",
  "Budget": "Pōpè",
  "left": "pípỳ",
  "in and out": "pò pē pȳpí",
  "monthly by category": "pūpù pà pēpepo",
  "debts and goals": "popà pē pipē",
  "one off": "pòpi pīpō",
  "monthly": "pūpù",
  "bills": "pipó",
  "costs": "pōpá",
  "debts": "popà",
  "goals": "pipē",
  "nothing here yet. add a bill, a debt or a goal above": "pòpā pōpỳ pōpí. pápā pipé, pōpā pō popī pỳpà",
  "only you see this unless you switch it on for someone in sharing. paid commissions count as money in": "pèpy pīpī pypí pýpèpì pīpī pópípà pù pá pōpūpy pò pípó. pōpēpó pēpùpypȳ pàpa pèpé pōpó pò",
  "Gesture": "Pàpùpí",
  "timed poses, draw fast, the next one drops in on its own": "pūpāpī pàpō, pōpé papỳpy, pēpā pòpi pipỳ pò pù pìpȳ",
  "source": "pípypȳ",
  "full body": "pàpà popo",
  "my refs": "pupe pēpī",
  "each pose": "pùpá pōpy",
  "pause": "pòpē",
  "skip": "pòpȳ",
  "Japanese": "Pīpépý",
  "learning": "pōpupe",
  "show answer": "pūpà pépépe",
  "again": "pépa",
  "hard": "pùpā",
  "good": "pípo",
  "easy": "pēpỳ",
  "space shows the answer, 1 to 4 grades it": "popū pópà pépépe, 1 4 pèpèpá",
  "decks": "pāpō",
  "new cards a day": "pépē pāpē pūpó",
  "study, new cards come in this order": "pupù, pépē pāpē pēpì pò pýpy",
  "Characters": "Pēpópypa",
  "chi look": "chi pōpù",
  "no characters match": "pōpō pēpópypa pupī",
  "new character": "pépē pípìpỳ",
  "name": "pīpé",
  "tags, comma separated": "pípā, pèpȳ pàpīpi",
  "sheet picture": "pùpū pīpē",
  "character file": "pípìpỳ pāpù",
  "chi saw": "chi púpo",
  "use as picture": "pàpī pèpé pīpē",
  "add pic": "pápā pape",
  "drop pic": "pàpý pape",
  "merge": "pypú",
  "merge into": "pypú pỳpú",
  "pick the same character under another name. its pictures and anything this sheet is missing move here, and imports will update this sheet from then on.": "pípī pápú pípìpỳ pȳpì pipipỳ pīpé. pīpē pē pypúpo pùpū pòpūpè pỳpe pōpỳ, pē popopē pepò pūpāpá pùpū pú pìpé pù.",
  "Calendar": "Pépàpē",
  "Maps": "Pāpō",
  "near me": "pèpì pèpa",
  "maps needs a google key": "pāpō pīpéo pápópȳ pūpỳ",
  "in console.cloud.google.com make a project and turn on billing": "pò pīpópē.pȳpe.pápópȳ.púpà pópē pápàpō pē popēpa pù pūpépi",
  "turn on": "popēpa pù",
  "and": "pē",
  "make an api key under credentials, and limit it to those three apis": "pópē pupā pūpỳ pȳpì pópȳpìpū, pē pupá pòpú pèpù pàpú",
  "paste it in setup, keys, google maps. or put it on the server as GOOGLE_MAPS_API_KEY": "pūpū pò pìpu, pȳpē, pápópȳ pāpō. pō popè pù papýpé pèpé Pápópȳ_Pāpō_Pupā_Pūpỳ",
  "open setup": "pópū pìpu",
  "search this area": "pépý pūpȳ",
  "route here": "pōpo pōpỳ",
  "route from": "pōpo pú",
  "remove": "popápu",
  "nearby": "papȳpè",
  "route": "pōpo",
  "best rated": "pīpú pìpà",
  "most reviews": "papó pìpepá",
  "closest": "pūpúpé",
  "parking first": "pépèpū pèpā",
  "add where you start and where you're going. places from nearby can go in with their route button": "pápā pípe pīpī pápó pē pípe pīpī pípepū. pēpē pú papȳpè pāpè pìpý pò pī pépō pōpo pópupí",
  "from here": "pú pōpỳ",
  "home": "pōpì",
  "best order for the middle stops": "pīpú pýpy pá pāpípí pópáp",
  "plan route": "pīpá pōpo",
  "time": "pūpāpī",
  "distance": "púpōpā",
  "stops": "pópáp",
  "pins": "pāpī",
  "tap drop a pin, then tap the map where it goes": "pēpú pàpý pāpī, pìpé pēpú pāpō pípe pepù",
  "pin where i am": "pāpī pípe pipè papu",
  "places": "pēpē",
  "star a place from nearby to keep it here": "pèpò pēpē pú papȳpè pépu pōpỳ",
  "routes": "pépápé",
  "plan a route and save it to keep it here": "pīpá pōpo pē pāpí pépu pōpỳ",
  "only you see these unless you switch places and routes on for someone in sharing": "pèpy pīpī pypí pùpī pýpèpì pīpī pópípà pēpē pē pépápé pù pá pōpūpy pò pípó",
  "tape": "pípīpi",
  "Scans": "Pỳpý",
  "photo": "pápòpé",
  "scan": "popā",
  "on this pc": "pù pépà",
  "upload all": "pūpòp pāpū",
  "scans the brother left in the inbox folder that the web cannot see yet": "pỳpý popúpō pípỳ pò pepe popùpà pēpo pīpāpó pypí pōpí",
  "no scans yet. scan on the pc, or snap a photo of a sketch": "pōpō pỳpý pōpí. popā pù pépà, pō pàpè pápòpé pỳpìpò",
  "Doodles": "Popopá",
  "paste": "pūpū",
  "no doodles yet. paste one, or add pictures from your phone": "pōpō popopá pōpí. pūpū pòpi, pō pápā pīpē pú pīpī pypì",
  "doodle": "pūpèpo",
  "title": "pipùpó",
  "drawn by": "pípé pà",
  "day": "pūpó",
  "characters in it": "pēpópypa pò",
  "undo mine": "pōpà pupú",
  "newer": "pépē",
  "older": "pópý",
  "Server": "Papýpé",
  "reload caddy": "pipīpé pypé",
  "memory": "pápàpè",
  "load": "pāpi",
  "bots up": "pèpī pupỳ",
  "Logs": "Pìpè",
  "nothing happened yet, it shows up here live": "pòpā pāpipe pōpí, pópà pupỳ pōpỳ pépū",
  "billing": "pūpépi",
  "month": "pūpù",
  "tags": "pípā",
  "key": "pūpỳ",
  "tagging": "pipípo",
  "budget": "pōpè",
  "batch": "pýpī",
  "by model": "pà popìpā",
  "by person": "pà pīpū",
  "estimates from the token counts claude sends back. billing has the real bill": "pepūpa pú púpu pèpùpí pòpāpà pēpīp pàpā. pūpépi pepō pýpȳ pipé",
  "People": "Pīpūpū",
  "let in": "pỳpa pò",
  "their chi": "pépō chi",
  "yours": "pīpī",
  "everyone talks to chi on your claude key. they never see it, their chats and memory stay their own, and their share of the bill is counted here": "pāpāpu pupo chi pù pīpī pòpāpà pūpỳ. pìpì pỳpí pypí, pépō pēpù pē pápàpè pùpò pépō pìpȳ, pē pépō pípó pipé pèpòpy pōpỳ",
  "let someone in": "pỳpa pōpūpy pò",
  "they sign in with discord. if they try before you add them, the page shows them their id to send you": "pìpì pípì pò pī pypàpà. pupà pìpì popa pupēpȳ pīpī pápā pōpe, pìpe pópà pōpe pépō pepà pēpīp pīpī",
  "only you": "pèpy pīpī",
  "Sharing": "Pípó",
  "pick what each person can see. they can only read it, never change it. it shows up next to their own notes, pings and the rest, and their chi can read it too": "pípī pòpí pùpá pīpū pāpè pypí. pìpì pāpè pèpy popó, pỳpí pȳpúpe. pópà pupỳ pēpā pépō pìpȳ pépù, pápý pē pápy, pē pépō chi pāpè popó pēpe",
  "shared with you": "pípópē pī pīpī",
  "nobody shares anything with you yet": "pupipy pípó pypúpo pī pīpī pōpí",
  "Setup": "Pìpu",
  "assistant": "pòpòpó",
  "anthropic api key": "pepèpì pupā pūpỳ",
  "chat model": "pēpù popìpā",
  "ref tagging model": "pēpī pipípo popìpā",
  "monthly tagging budget, usd": "pūpù pipípo pōpè, papī",
  "tagging pauses once the month's estimate passes the budget. 0 means no cap. claude-haiku-4-5 tags cheaper": "pipípo pòpē pèpá pūpù pypype pūpópò pōpè. 0 pỳpò pōpō pāpó. pòpāpà-pàpỳpī-4-5 pípā pypēpā",
  "currency": "pápopè",
  "yours only. the sign shown on comms, money stats and the budget. auto reads it from what you type on commissions. it saves as soon as you pick": "pīpī pèpy. pípì pipí pù pupē, pōpó pèpē pē pōpè. pópūpȳ pypu pú pòpí pīpī popé pù pēpùpypȳ. pāpí pèpé pȳpú pèpé pīpī pípī",
  "log into the plane tab, then connect": "pípȳ pỳpú popu pupepý, pìpé pēpēpú",
  "open plane, make an api token in profile settings, paste it here": "pópū popu, pópē pupā púpu pò pìpipȳ pópí, pūpū pōpỳ",
  "connect plane": "pēpēpú popu",
  "workspace slug": "pupupá pēpōpi",
  "api key": "pupā pūpỳ",
  "load boards": "pāpi pūpē",
  "chix bot": "pypa púpỳ",
  "api url": "pupā pēpu",
  "your discord user id": "pīpī pypàpà pīpū pepà",
  "browser key, optional": "pépàpé pūpỳ, pópȳpì",
  "the api key searches places and plans routes from the server. the map itself loads with the browser key if there is one, otherwise the api key. GOOGLE_MAPS_API_KEY on the server wins over this": "pupā pūpỳ pépý pēpē pē pīpá pépápé pú papýpé. pāpō púpìpé púpe pī pépàpé pūpỳ pupà pȳpà pòpi, popúpu pupā pūpỳ. Pápópȳ_Pāpō_Pupā_Pūpỳ pù papýpé pāpípý papé",
  "server": "papýpé",
  "bots folder": "pèpī popùpà",
  "sudo password": "pỳpù pípìpā",
  "ssh host": "pópu pipȳ",
  "ssh user": "pópu pīpū",
  "ssh key file": "pópu pūpỳ pāpù",
  "browser extension": "pépàpé púpīpē",
  "paste this into the extension options as the cloud token. it's yours, so saves land as you": "pūpū pỳpú púpīpē popépu pèpé pȳpe púpu. pīpī, pepé pāpí pépápú pèpé pīpī",
  "chrome: unzip over the old folder and hit reload in chrome://extensions. firefox: open the file in firefox and allow it, it updates itself after that": "pýpýpỳ: pūpúpē papé pupū popùpà pē pīpe pipīpé pò pýpýpỳ://pōpȳpypì. pīpípù: pópū pāpù pò pīpípù pē papá, pèpīpo púpìpé pēpò",
  "your extension token": "pīpī púpīpē púpu",
  "copy": "pūpēp",
  "new token": "pépē púpu",
  "links": "pàpe",
  "one per line as name | url. they open inside the desk app, or in a new tab on the web": "pòpi pīpȳ pòpu pèpé pīpé | pēpu. pìpì pópū popāpe pýpòpa pipò, pō pò pépē pupepý pù pēpo",
  "desk": "pýpòpa",
  "cloud url": "pȳpe pēpu",
  "cloud token": "pȳpe púpu",
  "chrome extension token": "pýpýpỳ púpīpē púpu",
  "brother scan folder": "popúpō popā popùpà",
  "start with windows": "pápó pī papùpa",
  "security": "pōpīpù",
  "signs out every phone, tablet and browser that is logged in, including this one. use it if a device was lost or you think someone got in": "pípì pȳpí pipā pypì, pópepò pē pépàpé pōpàpō pò, pùpúpō pòpi. pàpī pupà pōpipa pēpi pópȳ pō pīpī pỳpu pōpūpy pípopỳ pò",
  "log out everywhere": "pípȳ pȳpí pòpipỳpó",
  "pick a look": "pípī pōpù",
  "background pattern": "pèpòpapȳ pēpópý",
  "film grain and fog": "pepì pipū pē pīpūp",
  "calm motion": "pìpỳ pypépō",
  "retry": "pūpupā",
  "toss": "pāpé",
  "logs": "pìpè",
  "paid on": "pōpēpó pù",
  "undo": "pōpà",
  "setup token": "pìpu púpu",
  "talk to chi": "pypī chi",
  "jot into inbox": "pupu pỳpú pepe",
  "whose budget": "pápípy pōpè",
  "what kind": "pòpí pīpý",
  "what it is": "pòpí",
  "ask chi…": "pípē chi…",
  "ask": "pípē",
  "refresh": "pipōpu",
  "find a task": "pépý pàpí",
  "just type, it saves itself": "pȳpé popé, pāpí púpìpé",
  "search notes": "pépý pépù",
  "back": "pàpā",
  "print": "pāpy",
  "# heading\n\nmarkdown works, autosave is on": "# pȳpūpā\n\npūpepú pipypú, pepipa pù",
  "search refs, tags, characters": "pépý pēpī, pípā, pēpópypa",
  "tile size": "papa pýpỳ",
  "add pictures": "pápā pīpē",
  "sort the feed": "pépe pèpè",
  "what you want to remember about this": "pòpí pīpī pīpí papýpō pèpỳ",
  "re-index": "púpa-pýpē",
  "open post": "pópū pùpỳ",
  "message chi": "pēpòp chi",
  "send": "pēpīp",
  "a sketch a day…": "pỳpìpò pūpó…",
  "how often": "pȳpȳ pūpú",
  "how many": "pȳpȳ pupé",
  "note, optional": "pépù, pópȳpì",
  "nudge me about…": "pōpìpì pèpa pèpỳ…",
  "kind": "pīpý",
  "set the time": "pepī pūpāpī",
  "minutes, like 40 or 1h30": "pūpī, pèpū 40 pō 1pépī30",
  "reset": "pùpō",
  "another tip": "pipipỳ pỳpū",
  "pick a colour": "pípī pīpó",
  "name, like sitting nsfw": "pīpé, pèpū pypópé papí",
  "piece": "pepá",
  "client (for the money stats)": "pupìpū (pá pōpó pèpē)",
  "amount": "pỳpépú",
  "due": "pōpa",
  "what they want drawn": "pòpí pìpì pīpí pípé",
  "previous month": "pùpúpá pūpù",
  "next month": "pēpā pūpù",
  "what is it": "pòpí",
  "owed": "púpē",
  "category": "pēpepo",
  "only refs matching… (optional)": "pèpy pēpī púpápi… (pópȳpì)",
  "add the characters from zirk.eu/oc": "pápā pēpópypa pú pypà.pīpàpi/pȳpò",
  "add the characters from the discord character channel": "pápā pēpópypa pú pypàpà pípìpỳ pipípi",
  "chi looks at character pictures and writes tags and a line about each, so she picks better characters and refs": "chi pépo pè pípìpỳ pīpē pē pȳpápū pípā pē pòpu pèpỳ pùpá, pepé pỳpé pūpōpé pépapè pēpópypa pē pēpī",
  "search name, race, likes, fetishes…": "pépý pīpé, pāpo, pypū, pipypī…",
  "search characters…": "pépý pēpópypa…",
  "reload": "pipīpé",
  "add an event": "pápā pýpa",
  "map": "pāpō",
  "name it: studio, mum's, good parking": "pīpé: pōpú, pỳpèpè, pípo pépèpū",
  "a note, optional": "pépù, pópȳpì",
  "colour": "pīpó",
  "sushi, art supplies, an address": "pápá, papi pupápī, papòpe",
  "how far": "pȳpȳ pópypá",
  "add a stop: address, postcode or place": "pápā pópáp: papòpe, pupùpe pō pēpē",
  "add stop": "pápā pópáp",
  "download": "pōpūp",
  "add the picture on your clipboard, or press ctrl+v": "pápā pīpē pù pīpī pópupī, pō pēpè papè+pipà",
  "search title, artist, character, tag…": "pépý pipùpó, pìpāpo, pípìpỳ, pípā…",
  "untitled": "pāpāpà",
  "type a name to add a character": "popé pīpé pápā pípìpỳ",
  "count a fap": "pàpa pýpepy",
  "console.anthropic.com, api keys": "pīpópē.pepèpì.púpà, pupā pȳpē",
  "the part after the domain": "pýpý pēpò pỳpypú",
  "maps javascript, places (new) and routes": "pāpō púpípépý, pēpē (pépē) pē pépápé",
  "only if the map should load with its own key": "pèpy pupà pāpō púpepú pāpi pī pìpȳ pūpỳ",
  "find a tool or place": "pépý pòpó pō pēpē",
  "what needs doing?": "pòpí pīpéo pypýpý?",
  "optional notes": "pópȳpì pépù",
  "index": "pýpē",
  "mark done": "pēpé pōpē",
  "status": "pépȳpe",
  "link a task": "pèpu pàpí",
  "find a plane or chix task": "pépý popu pō pypa pàpí",
  "take back the last one": "pȳpù pàpā pipō pòpi",
  "take out": "pȳpù pȳpí",
  "tasks": "pàpí",
  "Today": "Pūpá",
  "References": "Pēpī",
  "describe": "pùpāpá",
  "Ask ChiX": "Pípē Pypa",
  "gesture": "pàpùpí",
  "Gesture drawing": "Pàpùpí pōpé",
  "Session timer": "Pépúpȳ pāpà",
  "palette": "pípūpỳ",
  "Commissions": "Pēpùpypȳ",
  "maps": "pāpō",
  "Calculator": "Pȳpepýpy",
  "doodles": "popopá",
  "scans": "pỳpý",
  "Claude usage": "Pòpāpà pùpo",
  "people": "pīpūpū",
  "share": "pípó",
  "setup": "pìpu",
  "keys": "pȳpē",
  "themes": "pīpóp",
  "studio": "pōpú",
  "system": "pipýpē",
  "Home": "Pōpì",
  "Studio": "Pōpú",
  "Practice": "Pípū",
  "Ledger": "Pipȳpò",
  "Library": "Pīpù",
  "Core": "Pōpāp",
  "language": "pēpòpī",
  "yours only. the app's words and chi's replies follow it. it saves as soon as you pick": "pīpī pèpy. pipò púpè pē chi pépipà pāpūpo. pāpí pèpé pȳpú pèpé pīpī pípī",
  "Quick note": "Pàpá pépù",
  "Reminders": "Pīpà",
  "Ubuntu helpers": "Papỳpì púpýpi",
  "Quit": "Pỳpipī",
  "open {name}": "pópū {name}",
  "sort": "pépe",
  "this one is mine": "pòpi pupú",
  "reverse the order": "pèpōpī pýpy",
  "filter": "pypōpà",
  "{name} can sign in now. they start with chi, notes, pings, routines and goals": "{name} pāpè pípì pò pìpi. pìpì pápó pī chi, pépù, pápý, pūpý pē pipē",
};
